<template>
    <ProfileCard
        :id="resultId"
        :data-testid="dataTestid"
        :data-client-browse-profiles-product-tour="index"
        class="profile-card-scroll-margin"
        :loading="skeleton"
        :border-style="borderStyle"
        :profile="profileCardData"
        :link-href="$withLocalePrefix(profileCardLinkHref)"
        :link-target="profileCardLinkTarget"
        :global-matching-enabled="isBrowsingGlobalMatching"
        @portfolio-clicked="onPortfolioClicked"
    >
        <template #extra-actions>
            <FavoriteButton
                ref="favoriteButtonRef"
                :profile-id="profile.id"
                :first-name="profile.firstName"
                :last-name="profile.lastNameNormalized"
                :photo-absolute-url="profile.photo.absoluteUrl"
                :checked="false"
                data-testid="profile-card-favorite-btn"
            />
            <VJoyIconButton
                v-if="userHasAdminRole && profile.adminDetails"
                variant="admin"
                size="xxsmall"
                icon="logo-brew"
                @click.prevent="openAdminDetailsPanel(profile.adminDetails)"
            />
        </template>
    </ProfileCard>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import type {Profile} from '@profile-card/components/profile-card/types';
    import {storeToRefs} from 'pinia';
    import {useRoute} from 'vue-router';
    import {ProfileCard} from '#components';
    import {useAuth, useDataTracking, useGlobalMatching} from '#imports';
    import {VJoyIconButton} from '@maltjoy/core-vue';
    import type {AdminDetailsResponse, ProfileResponse} from '@api';
    import {eventBus} from '~/config/events';
    import {toProfileCardData} from '~/utils/profileCardMapper';
    import FavoriteButton from '../../pool-of-freelances/favorite-modal/FavoriteButton.vue';
    import {useProfilesStore} from '../../stores/profiles.store';
    import {isGeoLocationParams, type LocationParams, useSearchUrlStore} from '../../stores/search-url.store';
    import {useShortlistStore} from '../../stores/shortlist.store';

    const props = defineProps<{
        profile: ProfileResponse;
        skeleton: boolean;
        index: number;
        dataTestid?: string;
    }>();

    const route = useRoute();
    const authStore = useAuth();
    const {track} = useDataTracking();
    const searchUrlStore = useSearchUrlStore();
    const {searchUrlParams} = storeToRefs(searchUrlStore);
    const profilesStore = useProfilesStore();
    const {searchId} = storeToRefs(profilesStore);
    const shortlistStore = useShortlistStore();
    const {isBrowsingGlobalMatching} = useGlobalMatching();

    const profileCardData = computed<Profile>(() => {
        return toProfileCardData(props.profile, targetCity(searchUrlParams.value.locationDetails));
    });

    // Marlene Ingénieur développeur .NET/Azure 🚀 → Marlene-Ingenieur-developpeur-NET-Azure
    const resultId = computed(() =>
        `${props.profile.firstName} ${props.profile.headline}`
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .replace(/[^\w,]+/gu, '-')
            .trim(),
    );

    const profileCardLinkHref = computed(() => {
        if (props.profile.url.length > 0) {
            return `${props.profile.url}?${searchUrlStore.toUrlQueryParams({searchid: searchId.value}).toString()}`;
        }
        const currentUrlFocusedOnProfile = `${route.fullPath}#${resultId.value}`;
        return `/who-are-you?redirect=${encodeURIComponent(currentUrlFocusedOnProfile)}`;
    });

    const profileCardLinkTarget = computed(() => (props.profile.url.length > 0 ? '_blank' : undefined));

    function targetCity(locationDetails?: LocationParams) {
        if (locationDetails && isGeoLocationParams(locationDetails)) {
            return locationDetails.city;
        }
        return undefined;
    }
    const userHasAdminRole = authStore.hasEnoughPrivilege(['ROLE_ADMIN']);

    const borderStyle = computed(() => {
        if (shortlistStore.isInShortlist(props.profile.id)) {
            return 'SELECTED';
        } else if (props.profile.adminDetails?.visible === false && props.profile.adminDetails?.reactivable === false) {
            return 'ADMIN';
        } else {
            return undefined;
        }
    });

    function onPortfolioClicked() {
        track('profile_card_portfolio_viewed', {
            search_event_id: searchId.value,
            freelancer_account_id: props.profile.id,
        });
    }

    function openAdminDetailsPanel(adminDetails: AdminDetailsResponse) {
        if (adminDetails) {
            eventBus.emit('profile-card:admin:click', adminDetails);
        }
    }
</script>

<style scoped lang="scss">
    .profile-card-scroll-margin {
        scroll-margin-top: calc(var(--search-page-header-height) + var(--joy-core-spacing-6));
    }
</style>
